import * as React from 'react';
import { Box, CircularProgress, IconButton, Alert } from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { notification } from 'antd';
import { Close, Delete } from '@mui/icons-material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import download from 'downloadjs';
import { MainInput } from '../app/components/Styles';
import {
  createBazaar, updateBazaar, getBazaar, getProductsSimplified, deleteBazaar,
} from '../app/store/actions/dataActions';
import Hoverable from '../app/glass/Hoverable';
import { cardShell, fieldStyle, fieldLabel, pageTitle, redCta } from '../app/glass/styles';

const normalizeProductList = (products) => {
  if (Array.isArray(products)) return products;
  if (Array.isArray(products?.docs)) return products.docs;
  if (Array.isArray(products?.products)) return products.products;
  return [];
};

const sortProductsBySku = (productList) => {
  return [...normalizeProductList(productList)].sort((a, b) => {
    const skuA = (a?.sku || a?.externalId || '').toString().toLowerCase();
    const skuB = (b?.sku || b?.externalId || '').toString().toLowerCase();
    return skuA.localeCompare(skuB);
  });
};

const stockAmountOf = (product) => product?.stockAmount ?? 0;

export const CreateBazaar = ({
  createBazaar, updateBazaar, getBazaar, getProductsSimplified, deleteBazaar,
  products, bazaarRecord, user, isLoading, isWaiting,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEdit = Boolean(id);
  const fileInputRef = React.useRef(null);

  const [form, setForm] = React.useState({
    name: '', location: '',
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().add(1, 'day').format('YYYY-MM-DD'),
    startTime: '10:00', endTime: '22:00', fulfillmentMode: 'POS',
  });
  const [lineItems, setLineItems] = React.useState([]);
  const [kycQuestions, setKycQuestions] = React.useState([{ question: '', required: false }]);
  const [checkedProductIds, setCheckedProductIds] = React.useState([]);
  const [productSearch, setProductSearch] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [formLoaded, setFormLoaded] = React.useState(!isEdit);
  const [productsLoaded, setProductsLoaded] = React.useState(false);

  const bazaar = bazaarRecord?.bazaar;
  const canEdit = !isEdit || (bazaar && ['Pending', 'Accepted'].includes(bazaar.status));
  const productsSortedBySku = React.useMemo(() => sortProductsBySku(products), [products]);

  React.useEffect(() => { getProductsSimplified({}); }, []);
  React.useEffect(() => {
    if (products != null) setProductsLoaded(true);
  }, [products]);
  React.useEffect(() => {
    if (isEdit) getBazaar(id);
  }, [id]);

  React.useEffect(() => {
    if (bazaar && isEdit) {
      setForm({
        name: bazaar.name,
        location: bazaar.location,
        startDate: moment(bazaar.startDate).format('YYYY-MM-DD'),
        endDate: moment(bazaar.endDate).format('YYYY-MM-DD'),
        startTime: bazaar.startTime || '10:00',
        endTime: bazaar.endTime || '22:00',
        fulfillmentMode: bazaar.fulfillmentMode,
      });
      setLineItems(bazaar.lineItems?.map((li) => ({
        _id: li.product?._id || li.product,
        name: li.product?.name,
        sku: li.product?.sku || li.product?.externalId,
        quantity: li.quantityRequested,
        stockAmount: 0,
      })) || []);
      setKycQuestions(bazaar.kycQuestions?.length ? bazaar.kycQuestions : [{ question: '', required: false }]);
      setFormLoaded(true);
    }
  }, [bazaar, isEdit]);

  React.useEffect(() => {
    if (!productsSortedBySku.length || !lineItems.length) return;
    setLineItems((prev) => prev.map((item) => {
      const fromCatalog = productsSortedBySku.find((p) => p._id === item._id);
      if (!fromCatalog) return item;
      return { ...item, stockAmount: fromCatalog.stockAmount ?? 0 };
    }));
  }, [productsSortedBySku]);

  const parseExcelFile = React.useCallback((pickedFile) => {
    if (!pickedFile || !productsSortedBySku.length) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const header = parsedData[0];
        const items = [];
        parsedData.slice(1).forEach((row) => {
          const item = {};
          row.forEach((value, index) => { item[header[index]] = value; });
          const found = productsSortedBySku.find(
            (p) => p.sku === item['Product SKU'] || p.externalId === item['Product SKU']
          );
          if (found && item.Quantity > 0) {
            items.push({
              _id: found._id,
              name: found.name,
              sku: found.sku || found.externalId,
              quantity: parseInt(item.Quantity, 10),
              stockAmount: stockAmountOf(found),
            });
          }
        });
        if (items.length) setLineItems(items);
        else notification.warning({ message: t('bazaars.errors.required') });
      } catch (err) {
        notification.error({ message: err.message });
      }
    };
    reader.readAsArrayBuffer(pickedFile);
  }, [productsSortedBySku, t]);

  React.useEffect(() => {
    if (file) parseExcelFile(file);
  }, [file, parseExcelFile]);

  const acceptFile = (picked) => {
    if (!picked) return;
    const name = (picked.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      notification.error({ message: t('bazaars.uploadExcel') });
      return;
    }
    setFile(picked);
  };

  const handleExportToExcel = async () => {
    setIsDownloading(true);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Retail Products');
    worksheet.columns = [
      { header: 'Product SKU', key: 'sku' },
      { header: 'Product Name', key: 'name' },
      { header: 'Available in stock', key: 'available' },
      { header: 'Quantity', key: 'quantity' },
    ];
    productsSortedBySku.forEach((product) => {
      worksheet.addRow({
        sku: product.sku || product.externalId,
        name: product.name,
        available: stockAmountOf(product),
        quantity: 0,
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const fileName = `${user?.vendor?.brandName || 'ChainzRetail'} Products - ${moment().format('DD_MM_YYYY')}.xlsx`;
    download(new Blob([buffer], { type: mimeType }), fileName, mimeType);
    setIsDownloading(false);
  };

  const availableProducts = React.useMemo(
    () => productsSortedBySku.filter((product) => !lineItems.some((item) => item._id === product._id)),
    [productsSortedBySku, lineItems]
  );

  const filteredAvailableProducts = React.useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return availableProducts;
    return availableProducts.filter((product) => {
      const sku = (product.sku || product.externalId || '').toLowerCase();
      const name = (product.name || '').toLowerCase();
      return sku.includes(term) || name.includes(term);
    });
  }, [availableProducts, productSearch]);

  const toggleProductSelection = (productId) => {
    setCheckedProductIds((prev) => (
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    ));
  };

  const handleAddSelectedProducts = () => {
    const productsToAdd = availableProducts.filter((product) => checkedProductIds.includes(product._id));
    if (!productsToAdd.length) return;
    setLineItems((prev) => {
      const next = [...prev];
      productsToAdd.forEach((p) => {
        if (next.some((item) => item._id === p._id)) return;
        next.push({
          _id: p._id,
          name: p.name,
          sku: p.sku || p.externalId,
          quantity: 1,
          stockAmount: stockAmountOf(p),
        });
      });
      return next;
    });
    setCheckedProductIds([]);
  };

  const editItemQty = (idx, value) => {
    setLineItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], quantity: parseInt(value, 10) || 1 };
      return next;
    });
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    if (!canEdit) {
      notification.error({ message: t('bazaars.cannotEdit') });
      return;
    }
    if (!form.name || !form.location || !lineItems.length) {
      notification.error({ message: t('bazaars.errors.required') });
      return;
    }
    if (moment(form.endDate).isBefore(moment(form.startDate))) {
      notification.error({ message: t('bazaars.errors.required') });
      return;
    }
    const payload = {
      ...form,
      lineItems: lineItems.map((i) => ({ _id: i._id, quantity: i.quantity })),
      kycQuestions: form.fulfillmentMode === 'POS'
        ? kycQuestions.filter((q) => q.question.trim())
        : [],
    };
    if (isEdit) updateBazaar(id, payload, navigate);
    else createBazaar(payload, navigate);
  };

  const selectedCount = checkedProductIds.length;
  const addColor = selectedCount > 0 ? 'var(--tx)' : 'var(--mut)';
  const addBg = selectedCount > 0 ? 'var(--s08)' : 'var(--s03)';
  const canSave = lineItems.length > 0 && form.name && form.location && !isWaiting && canEdit;

  if ((isEdit && isLoading && !bazaar) || !productsLoaded || (isEdit && !formLoaded)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
        <CircularProgress sx={{ color: 'var(--red-tx)' }} />
      </Box>
    );
  }

  if (isEdit && bazaar && !canEdit) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ ...pageTitle, fontSize: 28 }}>{t('bazaars.edit')}</div>
        <div style={cardShell}>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('bazaars.cannotEdit')}</Alert>
          <button type="button" className="cz-chip red" onClick={() => navigate(`/bazaars/${id}`)}>
            {t('common.view')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Hoverable as="button" type="button" className="cz-chip" hover={{}} onClick={() => navigate('/bazaars')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            {t('bazaars.title')}
          </Hoverable>
          <div style={{ ...pageTitle, fontSize: 28 }}>
            {isEdit ? t('bazaars.edit') : t('bazaars.create')}
          </div>
        </div>

        <div style={cardShell}>
          <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 16 }}>{t('bazaars.eventDetails')}</div>
          <div className="cz-g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <div style={fieldLabel}>{t('bazaars.name')}</div>
              <input
                required
                style={fieldStyle}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('bazaars.name')}
              />
            </div>
            <div>
              <div style={fieldLabel}>{t('bazaars.location')}</div>
              <input
                required
                style={fieldStyle}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder={t('bazaars.location')}
              />
            </div>
            <div>
              <div style={fieldLabel}>{t('bazaars.startDate')}</div>
              <input
                type="date"
                required
                style={fieldStyle}
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
            <div>
              <div style={fieldLabel}>{t('bazaars.endDate')}</div>
              <input
                type="date"
                required
                style={fieldStyle}
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </div>
            <div>
              <div style={fieldLabel}>{t('bazaars.startTime')}</div>
              <input
                style={fieldStyle}
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                placeholder="10:00"
              />
            </div>
            <div>
              <div style={fieldLabel}>{t('bazaars.endTime')}</div>
              <input
                style={fieldStyle}
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                placeholder="22:00"
              />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ ...fieldLabel, marginBottom: 10 }}>{t('bazaars.mode')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`cz-chip ${form.fulfillmentMode === 'POS' ? 'on' : ''}`}
                onClick={() => setForm({ ...form, fulfillmentMode: 'POS' })}
              >
                {t('bazaars.modePosShort')}
              </button>
              <button
                type="button"
                className={`cz-chip ${form.fulfillmentMode === 'Outbound' ? 'on' : ''}`}
                onClick={() => setForm({ ...form, fulfillmentMode: 'Outbound' })}
              >
                {t('bazaars.modeOutboundShort')}
              </button>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--mut)', marginTop: 10, lineHeight: 1.5 }}>
              {t('bazaars.modeHelp')}
            </div>
          </div>
        </div>

        <div className="cz-g1" style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 20, alignItems: 'start' }}>
          <div style={cardShell}>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>{t('bazaars.addViaExcel')}</div>
            <div style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 20 }}>{t('bazaars.excelHint')}</div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => acceptFile(e.target.files?.[0])}
            />
            <Hoverable
              base={{
                borderRadius: 18,
                border: `1.5px dashed ${dragOver ? 'rgba(190,50,62,0.70)' : 'rgba(190,50,62,0.45)'}`,
                background: dragOver ? 'rgba(125,1,12,0.22)' : 'rgba(125,1,12,0.12)',
                padding: '34px 20px',
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                gap: 10,
                cursor: 'pointer',
                transition: 'background .15s, border-color .15s',
              }}
              hover={{ background: 'rgba(125,1,12,0.20)' }}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                acceptFile(e.dataTransfer.files?.[0]);
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c0303e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4" />
                <path d="m7 9 5-5 5 5" />
                <path d="M4 20h16" />
              </svg>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t('inventory.uploadXlsx')}</div>
              <div style={{ fontSize: 12, color: 'var(--mut)' }}>{t('inventory.dragDrop')}</div>
            </Hoverable>

            {file && (
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--tx2)' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                <IconButton size="small" onClick={() => setFile(null)} sx={{ color: 'var(--mut)' }}>
                  <Close fontSize="small" />
                </IconButton>
              </div>
            )}

            <Hoverable
              as="button"
              type="button"
              onClick={handleExportToExcel}
              disabled={isDownloading}
              base={{
                marginTop: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: 'var(--tx2)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                fontFamily: 'inherit',
              }}
              hover={{ color: 'var(--txs)' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M4 21h16" />
              </svg>
              {t('bazaars.downloadTemplate')}
            </Hoverable>
          </div>

          <div style={cardShell}>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>{t('bazaars.addManually')}</div>
            <div style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 18 }}>{t('bazaars.manualHint')}</div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '4px 8px 4px 18px',
                borderRadius: 999,
                background: 'var(--s04)',
                border: '1px solid var(--s10)',
                color: 'var(--mut)',
                marginBottom: 14,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.2-3.2" />
              </svg>
              <input
                style={{ ...fieldStyle, border: 'none', background: 'transparent', padding: '10px 8px', width: '100%' }}
                placeholder={t('inventory.searchProducts')}
                value={productSearch}
                onChange={({ target }) => setProductSearch(target.value)}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
              {availableProducts.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--mut)', padding: '8px 4px' }}>{t('inventory.allProductsAdded')}</div>
              ) : filteredAvailableProducts.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--mut)', padding: '8px 4px' }}>{t('inventory.noProductsMatchSearch')}</div>
              ) : (
                filteredAvailableProducts.map((product) => {
                  const isChecked = checkedProductIds.includes(product._id);
                  return (
                    <Hoverable
                      key={product._id}
                      onClick={() => toggleProductSelection(product._id)}
                      base={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '11px 14px',
                        borderRadius: 14,
                        background: isChecked ? 'rgba(125,1,12,0.22)' : 'var(--s03)',
                        border: `1px solid ${isChecked ? 'rgba(190,50,62,0.40)' : 'var(--s06)'}`,
                        cursor: 'pointer',
                      }}
                      hover={{ background: 'var(--s06)' }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                          borderRadius: 7,
                          display: 'grid',
                          placeItems: 'center',
                          background: isChecked ? 'linear-gradient(160deg, #9c0410, #7D010C)' : 'var(--s05)',
                          border: `1px solid ${isChecked ? 'rgba(190,50,62,0.60)' : 'rgba(255,255,255,0.20)'}`,
                        }}
                      >
                        {isChecked && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--red-tx)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m5 13 4 4 10-10" />
                          </svg>
                        )}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--mut2)' }}>{product.sku || product.externalId}</div>
                      <div style={{ fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {product.name}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--mut)', flexShrink: 0 }}>
                        {stockAmountOf(product)}
                      </div>
                    </Hoverable>
                  );
                })
              )}
            </div>

            {lineItems.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12.5, color: 'var(--mut2)', marginBottom: 8 }}>
                  {t('bazaars.itemsSelected', { count: lineItems.length })}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {lineItems.map((product, idx) => {
                    const overStock = product.quantity > (product.stockAmount ?? 0);
                    return (
                      <div
                        key={product._id || idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 12px',
                          borderRadius: 14,
                          background: 'var(--s03)',
                          border: `1px solid ${overStock ? 'rgba(190,50,62,0.45)' : 'var(--s08)'}`,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, color: 'var(--mut2)' }}>{product.sku}</div>
                          <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {product.name}
                          </div>
                          <div style={{ fontSize: 11, color: overStock ? 'var(--red-tx2)' : 'var(--mut)', marginTop: 2 }}>
                            {t('bazaars.availableInStock')}: {product.stockAmount ?? 0}
                          </div>
                        </div>
                        <MainInput
                          required
                          type="number"
                          inputProps={{ min: 1 }}
                          value={lineItems[idx].quantity}
                          onChange={(event) => editItemQty(idx, event.target.value)}
                          sx={{ width: 88, '& .MuiInputBase-input': { py: 1, textAlign: 'center' } }}
                        />
                        <IconButton
                          onClick={() => setLineItems(lineItems.filter((i) => i._id !== product._id))}
                          size="small"
                          sx={{ color: 'var(--red-tx)' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={selectedCount === 0}
                onClick={handleAddSelectedProducts}
                style={{
                  flex: 1,
                  minWidth: 140,
                  textAlign: 'center',
                  padding: 12,
                  borderRadius: 999,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: addColor,
                  background: addBg,
                  border: '1px solid var(--s12)',
                  cursor: selectedCount > 0 ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                }}
              >
                {t('inventory.addSelected', { count: selectedCount })}
              </button>
            </div>
          </div>
        </div>

        {form.fulfillmentMode === 'POS' && (
          <div style={cardShell}>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>{t('bazaars.kycQuestions')}</div>
            <div style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 16 }}>{t('bazaars.modeHelp')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {kycQuestions.map((q, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    style={{ ...fieldStyle, flex: 1, minWidth: 200 }}
                    value={q.question}
                    onChange={(e) => {
                      const next = [...kycQuestions];
                      next[idx] = { ...next[idx], question: e.target.value };
                      setKycQuestions(next);
                    }}
                    placeholder={t('bazaars.questionPlaceholder')}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--tx2)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={(e) => {
                        const next = [...kycQuestions];
                        next[idx] = { ...next[idx], required: e.target.checked };
                        setKycQuestions(next);
                      }}
                    />
                    {t('bazaars.required')}
                  </label>
                  {kycQuestions.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => setKycQuestions(kycQuestions.filter((_, i) => i !== idx))}
                      sx={{ color: 'var(--red-tx)' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="cz-chip"
              style={{ marginTop: 14 }}
              onClick={() => setKycQuestions([...kycQuestions, { question: '', required: false }])}
            >
              {t('bazaars.addQuestion')}
            </button>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {isEdit && canEdit && (
            <button type="button" className="cz-chip" onClick={() => deleteBazaar(id, navigate)}>
              {t('common.delete')}
            </button>
          )}
          <button type="button" className="cz-chip" onClick={() => navigate(isEdit ? `/bazaars/${id}` : '/bazaars')}>
            {t('common.cancel')}
          </button>
          <Hoverable
            as="button"
            type="submit"
            disabled={!canSave}
            base={{
              ...redCta,
              opacity: canSave ? 1 : 0.55,
              cursor: canSave ? 'pointer' : 'default',
            }}
            hover={canSave ? { background: 'linear-gradient(160deg, rgba(156,4,16,0.65), rgba(100,1,10,0.50))' } : {}}
          >
            {isWaiting ? <CircularProgress size={18} sx={{ color: 'var(--red-tx)' }} /> : t('bazaars.saveRequest')}
          </Hoverable>
        </div>
      </div>
    </form>
  );
};

const mapStateToProps = (state) => ({
  products: state.records.products,
  bazaarRecord: state.records,
  user: state.auth?.user,
  isLoading: state.records.isLoading,
  isWaiting: state.wait.isLoading,
});

export default connect(mapStateToProps, {
  createBazaar, updateBazaar, getBazaar, getProductsSimplified, deleteBazaar,
})(CreateBazaar);
